import { Col, Modal, Row } from 'antd';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import CustomButton from '../../../../shared/components/buttons/custom-button/custom-button';
import IconWrapper from '../../../../shared/components/icon-wrapper/icon-wrapper';
import PageSpinner from '../../../../shared/components/page-spinner/page-spinner';
import { CloseIcon } from '../../../../shared/icons/close-icon';
import { useGetSignRequestDocumentMetadataQuery } from '../../../archive/api/archive-api';
import LazyModal, { LazyModalComponentProps } from '../../../ui/components/lazy-modal';
import { closeModal } from '../../../ui/store/ui-slice';
import { signRequestMetadataModalId } from '../../constants/constants';
import SignRequestMetadataItemForm from '../sign-request-metadata-item-form/sign-request-metadata-item-form';
import styles from './sign-request-metadata-modal.module.css';

interface SignRequestMetadataModalContentProps {
  documentId?: string;
}

const SignRequestMetadataModalContent = ({
  isOpen,
  afterClose,
  documentId,
}: LazyModalComponentProps & SignRequestMetadataModalContentProps): JSX.Element => {
  const screens = useBreakpoint();
  const { t } = useTranslation(['sign-requests', 'common']);
  const dispatch = useDispatch();
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const { data: documentMetadata, isLoading } = useGetSignRequestDocumentMetadataQuery(documentId!, {
    skip: !documentId,
  });
  const visibleDocumentMetatada = useMemo(() => documentMetadata?.filter(({ visible }) => visible), [documentMetadata]);

  const closeMetadataModal = () => dispatch(closeModal(signRequestMetadataModalId));

  return (
    <Modal
      className={styles.metadataModal}
      title={t('signRequestMetadataModal.metadata')}
      open={isOpen}
      onCancel={closeMetadataModal}
      maskClosable={false}
      closable={!isLoading}
      closeIcon={
        <IconWrapper>
          <CloseIcon />
        </IconWrapper>
      }
      afterClose={afterClose}
      width={960}
      footer={
        screens.sm && [
          <CustomButton type="primary" key="back" onClick={closeMetadataModal} disabled={isLoading}>
            {t('action.close', { ns: 'common' })}
          </CustomButton>,
        ]
      }
    >
      {isLoading ? (
        <PageSpinner />
      ) : (
        <>
          {documentMetadata && documentMetadata.length > 0 && visibleDocumentMetatada ? (
            <Row className={styles.previewDataRow}>
              {documentMetadata?.map(
                (metaData, index) =>
                  metaData.visible && (
                    <Col key={index} xs={24} md={8} className={styles.previewDataColumn}>
                      <SignRequestMetadataItemForm metadata={metaData} documentId={documentId} />
                    </Col>
                  ),
              )}
            </Row>
          ) : (
            <div>{t('signRequestMetadataModal.noMetadata')}</div>
          )}
        </>
      )}
    </Modal>
  );
};

const SignRequestMetadataModal = (): JSX.Element => {
  return (
    <LazyModal
      modalId={signRequestMetadataModalId}
      Component={props => <SignRequestMetadataModalContent {...props} />}
    />
  );
};

export default SignRequestMetadataModal;
